import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { publicApi } from "../lib/api";
import { useLenis } from "../lib/useLenis";
import { DEFAULT_SITE_DATA } from "../constants/defaultSiteData";
import SEO from "../components/site/SEO";
import Navbar from "../components/site/Navbar";
import ContactRail from "../components/site/ContactRail";
import Hero from "../components/site/Hero";
import Marquee from "../components/site/Marquee";
import LoadingScreen from "../components/site/LoadingScreen";
import { Problem, Positioning, Journey, Experience } from "../components/site/Sections1";
import { Build, System, Services, Storytelling } from "../components/site/Sections2";
import { Work, Solutions, Industries } from "../components/site/Sections3";
import { Founder, Why, NotPromise, Process } from "../components/site/Sections4";
import { GrowthCheck, Clients, Contact, Footer } from "../components/site/Sections5";

export default function Home() {
  useLenis();
  const [data, setData] = useState(DEFAULT_SITE_DATA);
  const [loading, setLoading] = useState(true);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    let active = true;
    const maxWait = setTimeout(() => {
      if (active) setLoading(false);
    }, 450);

    publicApi
      .get("/site")
      .then((r) => {
        if (active && r.data?.content) {
          setData(r.data);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      clearTimeout(maxWait);
    };
  }, []);

  const c = data.content || DEFAULT_SITE_DATA.content;

  return (
    <div className="bg-white" data-testid="home-page">
      <AnimatePresence>
        {loading && (
          <motion.div
            key="loading-screen"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="fixed inset-0 z-[100]"
          >
            <LoadingScreen />
          </motion.div>
        )}
      </AnimatePresence>
      <SEO
        title="Integrated Advertising, Multimedia & Digital Marketing"
        description="ADMALL INDIA connects brand strategy, creative, multimedia production, digital marketing and performance marketing under one accountable partner."
        canonical="https://admallindia.com/"
      />
      <motion.div
        style={{ scaleX: progress }}
        className="fixed left-0 top-0 z-[60] h-1 w-full origin-left bg-[#FF5722]"
        data-testid="scroll-progress"
      />
      <Navbar logo={c.logo_url} />
      <ContactRail settings={c.settings} />

      <main>
        <Hero hero={c.hero} />
        <Marquee marquee={c.marquee} />
        <Problem data={c.problem} />
        <Positioning data={c.positioning} />
        <Journey data={c.journey} />
        <Experience data={c.experience} />
        <Build data={c.build} />
        <System data={c.system} />
        <Services services={data.services} />
        <Storytelling data={c.storytelling} />
        <Work caseStudies={data.case_studies} />
        <Solutions solutions={data.solutions} />
        <Industries industries={data.industries} />
        <Founder data={c.founder} />
        <Why data={c.why} />
        <NotPromise data={c.not_promise} />
        <Process data={c.process} />
        <GrowthCheck data={c.growth_check} />
        <Clients data={c.clients} />
        <Contact data={c.contact} settings={c.settings} />
      </main>

      <Footer content={c} />
    </div>
  );
}
